import { PencilIcon, Cog6ToothIcon } from "@heroicons/react/24/outline";
import FacilityAvatar from "../shared/FacilityAvatar";
import StatusBadge from "../shared/StatusBadge";
import { formatPhoneDisplay } from "../../utils/phoneMask";
import { COUNTRIES } from "../../constants/facility.constants";

// The header is a static table and the rows live in their own scroll area; both
// use the same fixed column widths (and the same scrollbar gutter) so they line up.
const COLUMNS = [
  { key: "id", label: "ID", width: "w-[6%]" },
  { key: "facility", label: "Facility", width: "w-[21%]" },
  { key: "address", label: "Address", width: "w-[21%]" },
  { key: "timezone", label: "Timezone", width: "w-[14%]" },
  { key: "phone", label: "Phone", width: "w-[13%]" },
  { key: "status", label: "Status", width: "w-[11%]" },
  { key: "actions", label: "Actions", width: "w-[14%]" },
];

const FacilityTable = ({ facilities, isLoading, onRowClick, onEdit }) => (
  <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl bg-white shadow-[0_1px_3px_rgba(20,18,43,0.04),0_8px_24px_rgba(20,18,43,0.05)]">
    {/* Static header: never scrolls. It reserves the same scrollbar gutter as the
        rows area below so the columns stay perfectly aligned. */}
    {/* Small screens: the header and rows scroll sideways together */}
    <div className="table-scroll flex min-h-0 flex-1 flex-col overflow-x-auto">
    <div className="flex min-h-0 min-w-[1100px] flex-1 flex-col xl:min-w-[900px]">
    <div className="table-scroll flex-shrink-0 overflow-hidden bg-[#F5F2FF]" style={{ scrollbarGutter: "stable" }}>
      <table className="w-full table-fixed border-collapse text-left text-sm">
        <thead>
          <tr className="h-[46px] text-xs font-semibold uppercase tracking-wide text-gray-500">
            {COLUMNS.map((column) => (
              <th key={column.key} className={`${column.width} pl-[26px] pr-3 text-left`}>
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
      </table>
    </div>

    {/* Only the rows scroll */}
    <div className="table-scroll min-h-0 flex-1 overflow-x-hidden overflow-y-auto" style={{ scrollbarGutter: "stable" }}>
      <table className="w-full table-fixed border-collapse text-left text-sm">
        <tbody>
          {isLoading ? (
            <tr>
              <td colSpan={COLUMNS.length} className="pl-[26px] pr-3 py-10 text-center text-gray-500">
                Loading facilities...
              </td>
            </tr>
          ) : facilities.length === 0 ? (
            <tr>
              <td colSpan={COLUMNS.length} className="pl-[26px] pr-3 py-10 text-center text-gray-500">
                No facilities found.
              </td>
            </tr>
          ) : (
            facilities.map((facility) => {
              const callingCode = COUNTRIES.find((country) => country.name === facility.country)?.callingCode;
              return (
              <tr
                key={facility.id}
                onClick={() => onRowClick(facility)}
                className="cursor-pointer border-b border-gray-100 bg-white last:border-b-0 hover:bg-gray-50"
              >
                <td className={`${COLUMNS[0].width} pl-[26px] pr-3 py-4 text-left align-middle text-gray-500`}>
                  #{facility.facility_number}
                </td>
                <td className={`${COLUMNS[1].width} pl-[26px] pr-3 py-4 text-left align-middle`}>
                  <div className="flex items-center gap-3">
                    <FacilityAvatar name={facility.name} logoUrl={facility.logo_urls?.desktop_app_logo} size="h-9 w-9" />
                    <div className="min-w-0">
                      <div className="truncate font-semibold text-gray-900">{facility.name}</div>
                      <div className="truncate text-xs text-gray-500">{facility.primary_domain || "-"}</div>
                    </div>
                  </div>
                </td>
                <td className={`${COLUMNS[2].width} pl-[26px] pr-3 py-4 text-left align-middle text-gray-600`}>{facility.address || "-"}</td>
                <td className={`${COLUMNS[3].width} pl-[26px] pr-3 py-4 text-left align-middle text-gray-600`}>{facility.timezone || "-"}</td>
                <td className={`${COLUMNS[4].width} pl-[26px] pr-3 py-4 text-left align-middle text-gray-600`}>
                  {facility.phone_number
                    ? `${callingCode ? `+${callingCode} ` : ""}${formatPhoneDisplay(facility.phone_number)}`
                    : "-"}
                </td>
                <td className={`${COLUMNS[5].width} pl-[26px] pr-3 py-4 text-left align-middle`}>
                  <StatusBadge isActive={facility.is_active} />
                </td>
                <td className={`${COLUMNS[6].width} pl-[26px] pr-3 py-4 text-left align-middle`}>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEdit(facility);
                      }}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                      title="Edit facility"
                    >
                      <PencilIcon className="h-3.5 w-3.5" />
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onRowClick(facility);
                      }}
                      className="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                      title="Facility settings"
                    >
                      <Cog6ToothIcon className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
    </div>
    </div>
  </div>
);

export default FacilityTable;
